import React, { useState } from "react"
import PropTypes from "prop-types"
import Button from "@mui/material/Button"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"
import ReorderIcon from "@mui/icons-material/Reorder"
import VisibilityIcon from "@mui/icons-material/Visibility"
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableContainer from "@mui/material/TableContainer"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import Chip from "@mui/material/Chip"
import LoadingScreen from "components/common/LoadingScreen"
import { Container, Draggable } from "react-smooth-dnd"
import { verifyResponse } from "utils/httpRequest"
import { exchangeArrayItem } from "utils/data"
import {
  getSubCategories,
  exchangeCategoryIndex,
  deleteCategory,
  deleteSubCategory,
  updateCategory,
} from "services/category"
import { UseConfirmDialog } from "components/customHooks"
import CategoryReorderProgramDlg from "./CategoryReorderProgram"
import CategoryReorderSubCategoryDlg from "./CategoryReorderSubcategory"
import UseNotificationDialog from "components/customHooks/NotificationDialog"
import { useSnackbar } from "notistack"
import { StyledCategoryItem } from "./Category.style"

function CategoryItem(props) {
  const { row, actions, access, loading } = props

  const {
    openCategoryDialog,
    openSubCategoryDialog,
    removeCategory,
    removeSubCategory,
    openConfirmDialog,
    setLoading,
    toggleCategoryVisibility,
  } = actions

  const [openProgramReorderDlg, programReorderDlg, programReorderDlgLoading] =
    CategoryReorderProgramDlg({ access })

  const [expanded, setExpanded] = React.useState(false)
  const [subCategoryLoaded, setSubCategoryLoaded] = useState(false)
  const [subCategory, setSubCategory] = useState([])
  const isVisible = row.pubStatus === 1

  const loadSubCategory = async (categoryId) => {
    try {
      setLoading(true)
      const subCate = await getSubCategories(categoryId)
      setSubCategory(subCate)
    } catch (e) {
      console.error("getSubCategories error: ", e)
    } finally {
      setSubCategoryLoaded(true)
      setLoading(false)
    }
  }

  const handleChange = (panel) => async (event, isExpanded) => {
    if (access.listSubCategoryAccess && !subCategoryLoaded) {
      await loadSubCategory(row.id)
    }
    setExpanded(isExpanded ? panel : false)
  }

  const [
    openSubCategoryReorderDlg,
    subCategoryReorderDlg,
    subCategoryReorderDlgLoading,
  ] = CategoryReorderSubCategoryDlg({
    access,
    reloadCallback: (categoryId) => {
      loadSubCategory(categoryId)
    },
  })

  return (
    <>
      <StyledCategoryItem>
        <Accordion
          expanded={expanded === row.id}
          onChange={handleChange(row.id)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <div className="category-summary">
              {row.iconUrl && (
                <img
                  src={row.iconUrl}
                  alt=""
                  style={{
                    height: 32,
                    objectFit: "contain",
                    width: 32,
                  }}
                />
              )}
              <Chip
                className="category-id-chip"
                label={`Category ID ${row.id}`}
                color="primary"
                size="small"
              />
              <Typography className="category-name">{row.name}</Typography>
              {row.fullName && (
                <Chip
                  className="category-fullname-chip"
                  label={row.fullName}
                  color="info"
                  size="small"
                />
              )}
              <Chip
                className="category-status-chip"
                label={isVisible ? "Visible" : "Hidden"}
                color={isVisible ? "success" : "default"}
                variant={isVisible ? "filled" : "outlined"}
                size="small"
              />
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div className="action-bar">
              {row.isOnboarding === 1 && (
                <Chip
                  label="Show Onboarding"
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
              {access.addSubCategoryAccess && (
                <Button
                  variant="contained"
                  size="small"
                  className="action-btn reorder"
                  startIcon={<ReorderIcon />}
                  onClick={() => {
                    openSubCategoryReorderDlg(subCategory, row.id)
                  }}
                >
                  Reorder Subcategories
                </Button>
              )}
              {access.addSubCategoryAccess && (
                <Button
                  variant="contained"
                  size="small"
                  className="action-btn add"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    openSubCategoryDialog(row.id, null, () => {
                      loadSubCategory(row.id)
                    })
                  }}
                >
                  Add Subcategory
                </Button>
              )}
              {access.deleteCategoryAccess && (
                <Button
                  variant="contained"
                  size="small"
                  className="action-btn delete"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    openConfirmDialog(async () => {
                      await removeCategory(row)
                    })
                  }}
                >
                  Delete
                </Button>
              )}
              {access.editCategoryAccess && (
                <Button
                  variant="contained"
                  size="small"
                  className={`action-btn ${isVisible ? "hide" : "show"}`}
                  startIcon={
                    isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />
                  }
                  onClick={() => {
                    toggleCategoryVisibility(row)
                  }}
                >
                  {isVisible ? "Hide" : "Show"}
                </Button>
              )}
              {access.editCategoryAccess && (
                <Button
                  variant="contained"
                  size="small"
                  className="action-btn edit"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    openCategoryDialog(row)
                  }}
                >
                  Edit
                </Button>
              )}
            </div>

            {access.listSubCategoryAccess && subCategory.length > 0 ? (
              <TableContainer className="subcategory-table">
                <Table size="small" aria-label="subcategory-list">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Subcategory Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell sx={{ width: "130px", textAlign: "center" }}>
                        Program Count
                      </TableCell>
                      <TableCell
                        align="right"
                        size="small"
                        sx={{ width: "420px", textAlign: "center" }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subCategory.map((subCate) => (
                      <TableRow key={subCate.id}>
                        <TableCell component="th" scope="row">
                          {subCate.id}
                        </TableCell>
                        <TableCell>{subCate.name}</TableCell>
                        <TableCell>{subCate.description}</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          {subCate.programCount}
                        </TableCell>
                        <TableCell align="right">
                          <div className="subcategory-actions">
                            {access.deleteSubCategoryAccess && (
                              <>
                                <Button
                                  variant="contained"
                                  size="small"
                                  className="action-btn order"
                                  startIcon={<ReorderIcon />}
                                  onClick={() => {
                                    openProgramReorderDlg(subCate)
                                  }}
                                >
                                  Program Order
                                </Button>

                                <Button
                                  variant="contained"
                                  size="small"
                                  className="action-btn delete"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => {
                                    openConfirmDialog(async () => {
                                      await removeSubCategory(
                                        subCate,
                                        loadSubCategory
                                      )
                                    })
                                  }}
                                >
                                  Delete
                                </Button>
                              </>
                            )}
                            {access.editSubCategoryAccess && (
                              <Button
                                variant="contained"
                                size="small"
                                className="action-btn edit"
                                startIcon={<EditIcon />}
                                onClick={() => {
                                  openSubCategoryDialog(
                                    row.id,
                                    subCate,
                                    function () {
                                      loadSubCategory(row.id)
                                    }
                                  )
                                }}
                              >
                                Edit
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <>
                <Typography
                  className="empty-subcategory-title"
                  variant="h6"
                  component="div"
                >
                  Subcategories
                </Typography>
                <p className="empty-subcategory">
                  No subcategories are available for this category yet.
                </p>
              </>
            )}
          </AccordionDetails>
        </Accordion>
      </StyledCategoryItem>
      {programReorderDlg}
      {subCategoryReorderDlg}
      <LoadingScreen
        loading={
          loading || programReorderDlgLoading || subCategoryReorderDlgLoading
        }
      />
    </>
  )
}

CategoryItem.propTypes = {
  row: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
}

const CategoryTable = ({ data, actions, access }) => {
  const { refreshCategories, setCategories } = actions
  const [loading, setLoading] = useState(false)

  const { enqueueSnackbar } = useSnackbar()

  const [ConfirmDialog, openConfirmDialog] = UseConfirmDialog({
    title: "Delete Confirm",
    description: "Are you sure want delete this ?",
    agreeText: "Yes",
    disagreeText: "No",
  })

  const [NotificationDlgContent, openNotificationDlg] = UseNotificationDialog()

  const removeCategory = async (category) => {
    setLoading(true)
    const response = await deleteCategory(category.id)
    setLoading(false)

    switch (response.data.rtnCode) {
      case 200:
        enqueueSnackbar("Category Removed", { variant: "success" })
        break
      case 20064:
        enqueueSnackbar(response.data.message, { variant: "warning" })
        break
      default:
        enqueueSnackbar("Remove Category Fail", { variant: "warning" })
    }
    refreshCategories()
  }

  const toggleCategoryVisibility = async (category) => {
    const nextPubStatus = category.pubStatus === 1 ? false : true
    setLoading(true)
    try {
      const response = await updateCategory({
        ...category,
        pubStatus: nextPubStatus,
        isOnboarding: category.isOnboarding === 1,
      })

      if (response.data.rtnCode === 200) {
        enqueueSnackbar(nextPubStatus ? "Category Shown" : "Category Hidden", {
          variant: "success",
        })
      } else {
        enqueueSnackbar(response?.data?.message || "Update Category Fail", {
          variant: "warning",
        })
      }
    } catch (error) {
      console.error("toggleCategoryVisibility error: ", error)
      enqueueSnackbar("Update Category Fail", { variant: "error" })
    } finally {
      setLoading(false)
      refreshCategories()
    }
  }

  const exchangeCategoryOrder = async (newOrderArray) => {
    const response = await exchangeCategoryIndex(newOrderArray)
    verifyResponse(response)
    if (response.rtnCode === 200) {
      await refreshCategories()
    } else {
      //
      enqueueSnackbar(`Category reordering fail, ${response.message}`, {
        variant: "warning",
      })
    }
  }

  const removeSubCategory = async (subCategory, reloadSubCategory) => {
    setLoading(true)
    const response = await deleteSubCategory(subCategory.id)
    setLoading(false)

    switch (response.data.rtnCode) {
      case 200:
        reloadSubCategory(subCategory.categoryId)
        enqueueSnackbar("SubCategory Removed", { variant: "success" })
        break
      case 20063:
        enqueueSnackbar(response.data.message, { variant: "warning" })
        break
      case 20067:
        enqueueSnackbar(response.data.message, { variant: "warning" })
        openNotificationDlg(
          <div>
            <h4>{`SubCategory '${subCategory.name}' still using by program : `}</h4>
            <ul>
              {response.data.data.map((cate) => (
                <li
                  key={`cate-title-${cate.id}`}
                >{`${cate.title} (Program ID: ${cate.id})`}</li>
              ))}
            </ul>
          </div>
        )
        break

      default:
        enqueueSnackbar("Remove SubCategory Fail", { variant: "warning" })
    }
    refreshCategories()
  }

  return (
    <Container
      onDrop={async (e) => {
        const updateArray = exchangeArrayItem({
          addedIndex: e.addedIndex,
          removedIndex: e.removedIndex,
          indexString: "categoryIndex",
          list: data,
        })
        setCategories(updateArray)
        exchangeCategoryOrder(
          updateArray.map((item) => {
            return { id: item.id, index: item.categoryIndex }
          })
        )
      }}
    >
      {data.map((row) => (
        <Draggable key={row.id}>
          <CategoryItem
            key={row.id}
            row={row}
            access={access}
            actions={{
              ...actions,
              removeCategory,
              removeSubCategory,
              toggleCategoryVisibility,
              openConfirmDialog,
            }}
            loading={loading}
          />
        </Draggable>
      ))}
      {ConfirmDialog}
      {NotificationDlgContent}
    </Container>
  )
}

CategoryTable.propTypes = {
  data: PropTypes.array.isRequired,
  actions: PropTypes.object.isRequired,
  access: PropTypes.object.isRequired,
}

export default CategoryTable
